<template>
  <div class="preview-progress-panel" :data-testid="testId">
    <sp-progress type="ellipsis" size="x-small"></sp-progress>
    <p class="preview-progress-panel__text" role="status">{{ loadingText }}</p>

    <template v-if="hasPercent">
      <sp-progress
        v-preserve-properties:value="percent"
        class="preview-progress-panel__bar"
        type="linear"
        size="micro"
        :aria-label="loadingText"
      ></sp-progress>
      <p class="preview-progress-panel__text preview-progress-panel__detail">{{ detailText }}</p>
    </template>
  </div>
</template>

<script setup>
/**
 * Waiting state for an SpAdmin background preview: an ellipsis spinner, the
 * loading sentence in a `status` live region, and — once the job reports row
 * counts — a determinate bar with the row counter and time estimate. The counter
 * sits outside the live region so its ticks are not announced.
 */
import { computed } from "vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";

const props = defineProps({
  /** The loading sentence; also labels the determinate bar. */
  loadingText: {
    type: String,
    required: true,
  },
  /** Row counter sentence, e.g. "400 of 8000 rows previewed". Empty hides it. */
  progressText: {
    type: String,
    required: true,
  },
  /** Time estimate sentence, e.g. "About 4 minutes remaining". Empty hides it. */
  remainingText: {
    type: String,
    required: true,
  },
  /** Completion percentage for the determinate bar; null until the job reports a total. */
  percent: {
    type: Number,
    default: null,
  },
  /** Optional data-testid threaded from the specific caller. */
  testId: {
    type: String,
    default: undefined,
  },
});

const hasPercent = computed(() => typeof props.percent === "number");
const detailText = computed(() => [props.progressText, props.remainingText].filter(Boolean).join(" · "));
</script>

<style scoped lang="scss">
.preview-progress-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-ref-scale-8);
  padding-block: var(--sp-ref-scale-24);

  &__text {
    font-size: var(--sp-sys-typescale-body-font-size);
    color: var(--sp-sys-color-on-surface-variant);
    margin: 0;
  }

  &__bar {
    width: min(24rem, 100%);
  }
}
</style>
